// External libraries
import Vue from 'vue'
import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { BrowserTracing } from '@sentry/tracing'
import Hotjar from 'vue-hotjar'
import VueMixpanel from 'vue-mixpanel'
import { i18n } from '@/i18n'

// Aliases
import {
    DEFAULT_LOCALE,
    TOAST_TYPES,
    HOTJAR_SITE_ID,
    SENTRY_ENV,
    SENTRY_KEY,
    REQUEST_TIMEOUTS
} from '@/constants'
import { getLocalStorageItem } from '@/services/localStorage'

// Relative imports
import { config } from '../config'

const USER_ACTION_TTL = 1000

/**
 * Configure axios defaults
 */
export function configureHttp () {
    axios.defaults.baseURL = config.getApiUrl()
    if (process.env.VUE_APP_API_WITH_CREDENTIALS === 'true') {
        axios.defaults.withCredentials = true
    }
    axios.defaults.headers.Accept = 'application/json'
    axios.defaults.headers['Accept-Language'] =
        getLocalStorageItem('locale') || DEFAULT_LOCALE

    // One persistent toast, shown only while requests are in flight.
    // A fresh user action (click/input/change/submit) within USER_ACTION_TTL
    // before a new request restarts the 30s countdown so the next
    // user-initiated action gets its own window — but a burst of requests
    // fired automatically (e.g. initial page load) shares a single countdown.
    let lastUserActionAt = 0
    const markUserAction = () => { lastUserActionAt = Date.now() }
    ;['click', 'input', 'change', 'submit'].forEach(evt => {
        document.addEventListener(evt, markUserAction, true)
    })

    const longRunning = {
        pendingCount: 0,
        timeoutId: null,
        toastInstance: null,

        startCountdown () {
            this.timeoutId = setTimeout(() => {
                this.timeoutId = null
                if (this.pendingCount > 0 && !this.toastInstance) {
                    this.toastInstance = Vue.toasted.show(
                        i18n.t('COMMON.LONG_RUNNING_REQUEST_MESSAGE'),
                        {
                            type: TOAST_TYPES.INFO,
                            duration: null
                        }
                    )
                }
            }, REQUEST_TIMEOUTS.LONG_RUNNING)
        },

        reset () {
            if (this.timeoutId) {
                clearTimeout(this.timeoutId)
                this.timeoutId = null
            }
            if (this.toastInstance) {
                this.toastInstance.goAway(0)
                this.toastInstance = null
            }
        },

        add () {
            const userInitiated = lastUserActionAt > 0 &&
                Date.now() - lastUserActionAt < USER_ACTION_TTL

            if (userInitiated) {
                this.reset()
                lastUserActionAt = 0
            }

            this.pendingCount++

            if (!this.timeoutId && !this.toastInstance) {
                this.startCountdown()
            }
        },

        remove () {
            this.pendingCount = Math.max(0, this.pendingCount - 1)
            if (this.pendingCount === 0) {
                this.reset()
            }
        }
    }

    axios.interceptors.request.use(requestConfig => {
        if (requestConfig.doNotTrackLongRunning !== true) {
            requestConfig._trackedLongRunning = true
            longRunning.add()
        }

        return requestConfig
    })

    axios.interceptors.response.use(
        response => {
            if (response.config._trackedLongRunning) {
                longRunning.remove()
            }
            return response
        },
        error => {
            if (error.config?._trackedLongRunning) {
                longRunning.remove()
            }
            return Promise.reject(error)
        }
    )
}

/**
 * Manually update Accept-Language header
 */
export function configureHttpHeaderLang (lang) {
    axios.defaults.headers['Accept-Language'] = lang
}

/**
 * Configure Sentry error reporting
 */
export function configureSentry (router) {
    Sentry.init({
        Vue,
        dsn: SENTRY_KEY,
        environment: SENTRY_ENV,
        integrations: [
            new BrowserTracing({
                routingInstrumentation: Sentry.vueRouterInstrumentation(router),
                tracingOrigins: ['localhost', /^\//]
            })
        ],
        ignoreUrls: [
            /extensions\//i,
            /^chrome:\/\//i,
            /^chrome-extensions:\/\//i
        ],
        ignoreErrors: ['ReportingObserver [deprecation]', 'ResizeObserver'],
        tracesSampleRate: 1.0
    })
}

/**
 * Configure Hotjar tracking
 */
export function configureHotJar () {
    Vue.use(Hotjar, {
        id: HOTJAR_SITE_ID,
        isProduction: config.isProductionEnv()
    })
}

/**
 * Configure Mixpanel tracking
 */
export function configureMixpanel (store) {
    Vue.use(VueMixpanel, {
        token: process.env.VUE_APP_MIXPANEL_TOKEN,
        config: {
            api_host: 'https://api-eu.mixpanel.com',
            debug: !config.isProductionEnv(),
            persistence: 'localStorage'
        }
    })
    store.watch(
        // 1st Argument: A function that returns the data we want to watch.
        state => state.account.user,

        // 2nd Argument: The callback that executes when the data changes.
        (newUser, oldUser) => {
            // --- CASE 1: User is set (login or initial load) ---
            if (newUser && newUser.id) {
                // Only identify if the ID has changed (prevents redundant calls)
                if (!oldUser || newUser.id !== oldUser.id) {
                    Vue.prototype.$mixpanel.identify(newUser.id)
                }
            }
        },

        // 3rd Argument: Options
        {
            // `immediate: true` ensures the callback runs IMMEDIATELY on registration
            // with the current value of `store.state.account.user`.
            // This eliminates the need for a separate, initial call.
            immediate: true
        }
    )
}
